<template>
  <div class="item-execution-details-info">
    <!-- Top half: fields (left) + map (right) -->
    <div class="item-execution-details-info__top">
      <div class="item-execution-details-info__fields">
        <div class="item-execution-details-info__title">
          {{ translate('road-maintenance/temporary_regulation.basic_info') }}
        </div>
        <fields-renderer
          :config="fieldsConfig"
          :form-data="formData"
          :edit="false"
          :create-mode="false"
        />
      </div>

      <div class="item-execution-details-info__map">
        <item-execution-map-panel
          :custom-data="mapRoutes"
          :locations="mapLocations"
        />
      </div>
    </div>

    <!-- Bottom half: log table, full width, fills whatever's left -->
    <div class="item-execution-details-info__log">
      <loader :show="logLoader" />

      <data-table
        :config="logTableConfig"
        :show-toolbar="false"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import ItemExecutionMapPanel from './ItemExecutionMapPanel.vue'
import Loader from '@/global/components/loader/Loader.vue'
import { useTranslate } from '@/global/composables/useTranslate'
import { api } from '@/global/services/api'
import { isArray } from '@/global/utils/utils'
import { dateService, FORMATS } from '@/global/services/date'

const NS = 'road-maintenance/temporary_regulation/item_execution'
const FieldsRenderer = () => import('@/global/components/form-layout/FieldsRenderer.vue')
// DataTable pulls in AutocompleteField (via TableFiltersDialog) synchronously.
// FieldsRenderer above already needs that same AutocompleteField, but only
// gets it asynchronously - mixing both import strategies for a shared
// dependency in one file corrupts AutocompleteField's registration app-wide
// (breaks it on unrelated screens too). Keeping DataTable async as well
// avoids that split.
const DataTable = () => import('@/global/components/table/DataTable.vue')

const props = defineProps({
  formData: {
    type: Object,
    default: () => ({})
  }
})

const { translate } = useTranslate()

const mapRoutes = computed(() => {
  if (!props.formData?.polyline) {
    return []
  }

  return [{
    id: props.formData.id,
    polyline: props.formData.polyline
  }]
})

const mapLocations = computed(() => isArray(props.formData?.locations) ? props.formData.locations : [])

const logEntries = ref([])
const logLoader = ref(false)

async function fetchLog (execRouteId) {
  if (!execRouteId) {
    logEntries.value = []
    return
  }

  try {
    logLoader.value = true
    const { data } = await api('road-maintenance').get(`work-orders/exec-routes/${execRouteId}/log`)
    logEntries.value = isArray(data) ? data : []
  }
  catch (exception) {
    console.error(exception)
  }
  finally {
    logLoader.value = false
  }
}

// `created_at` comes back as a plain "YYYY-MM-DD HH:mm:ss" string (see
// ExecRouteController::log()), already converted to the request's timezone.
function getLogTime (entry) {
  return entry?.created_at ? dateService.formatCustom(entry.created_at, FORMATS.isoDatetime, FORMATS.timeShort) : '-'
}

function getLogDate (entry) {
  return entry?.created_at ? dateService.formatCustom(entry.created_at, FORMATS.isoDatetime, FORMATS.date) : '-'
}

watch(() => props.formData?.id, (id) => fetchLog(id), { immediate: true })

const logTableItems = computed(() => logEntries.value.map((entry, index) => ({
  ...entry,
  rn: index + 1,
  time: getLogTime(entry),
  date: getLogDate(entry)
})))

const logTableConfig = computed(() => ({
  fetchOnCreate: false,
  // Default `autoHeight: true` measures the table's available height ONCE via
  // getBoundingClientRect() and freezes it as an inline pixel style, with no
  // resize listener to re-measure it - stale until a full page refresh remounts
  // the component. `false` makes DataTable use `height: 100%` instead, which
  // the flex chain here (`.item-execution-details-info__log`) resolves live on
  // every resize.
  autoHeight: false,
  tableData: logTableItems.value,
  // Vuetify's default v-container padding would otherwise sit the table card
  // inset 16px from this wrapper's edge, out of alignment with the fields
  // box above - `globalStyle.containerStyle` is DataTable's own config hook
  // for that, so no `::v-deep` is needed here.
  globalStyle: {
    containerStyle: { padding: 0, height: '100%' }
  },
  tableConfig: {
    headers: [
      { label: translate(`${NS}.rn`), key: 'rn', always_visible: true },
      { label: translate(`${NS}.time`), key: 'time', always_visible: true },
      { label: translate('road-maintenance.date'), key: 'date', always_visible: true },
      { label: translate('road-maintenance/patrol-service.description'), key: 'description', always_visible: true },
      { label: translate('road-maintenance.user'), key: 'user', always_visible: true },
      { label: translate(`${NS}.comment`), key: 'comment', always_visible: true }
    ]
    // No `paginationConfig` on purpose - DataTable only renders <table-pagination>
    // when this key is present, and this log is small enough not to need it.
  }
}))

const fieldsConfig = computed(() => ({
  columnsCount: 2,
  fields: [
    {
      key: 'id',
      label: translate('road-maintenance/settings.id'),
      row: 1,
      visible: true,
      editable: false
    },
    {
      key: 'status_label',
      label: translate('road-maintenance/excavation-service.status'),
      row: 1,
      visible: true,
      editable: false
    },
    {
      key: 'crew_name',
      label: translate(`${NS}.crew_name`),
      row: 2,
      visible: true,
      editable: false
    },
    {
      key: 'vehicle_registration',
      label: translate(`${NS}.vehicle_registration`),
      row: 2,
      visible: true,
      editable: false
    },
    {
      key: 'responsible_person',
      label: translate(`${NS}.answer_person`),
      row: 3,
      visible: true,
      editable: false
    },
    {
      key: 'id',
      label: translate(`${NS}.work_order_id`),
      row: 3,
      visible: true,
      editable: false
    },
    {
      key: 'active_location_label',
      label: translate(`${NS}.active_item`),
      row: 4,
      colSpan: 2,
      visible: true,
      editable: false
    },
    {
      key: 'crew_members',
      label: translate(`${NS}.crew_members`),
      row: 5,
      visible: true,
      editable: false
    },
    {
      key: 'vehicle_label',
      label: translate(`${NS}.vehicle_label`),
      row: 5,
      visible: true,
      editable: false
    },
    {
      key: 'active_activity_label',
      label: translate(`${NS}.active_activity`),
      row: 6,
      colSpan: 2,
      visible: true,
      editable: false
    }
  ]
}))
</script>

<style scoped>
.item-execution-details-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 15px;
  /* This component's own root is rendered directly inside Content.vue's
     `.tab-content-div` (a `display: flex; flex-direction: column` element
     that already fills the tab's available height). A flex CHILD fills that
     kind of parent via `flex-grow`, not `height: 100%` (a percentage height
     needs the parent's height to already be a definite value through the
     whole chain - one link using content-based sizing anywhere above breaks
     it). `min-height: 0` is required alongside `flex: 1` or a flex item
     refuses to shrink below its content's natural size, which is what
     actually caused the earlier "blows up past the viewport" result. */
  flex: 1 1 auto;
  min-height: 0;
}

.item-execution-details-info__top {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: 24px;
  /* The map has its own fixed height below - it shouldn't stretch to match
     however tall the fields box ends up being. */
  align-items: start;
  flex: 0 0 auto;
}

.item-execution-details-info__fields {
  min-width: 0;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  padding: 16px;
  box-sizing: border-box;
}

.item-execution-details-info__title {
  font-family: 'Satoshi', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
}

.item-execution-details-info__map {
  min-width: 0;
  height: 400px;
}

.item-execution-details-info__log {
  position: relative;
  min-width: 0;
  /* Grows to fill whatever's left below the fields+map row, within the
     tab's own scrollable area - DataTable still needs a real (not "auto")
     height on its container to lay out its body, which `flex: 1` combined
     with `min-height` (a floor, not a magic viewport calc) provides here. */
  flex: 1 1 auto;
  min-height: 220px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
</style>
